import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import ExpenseForm from '../components/ExpenseForm';

interface Expense {
  id: number;
  date: string;
  amount: number;
  type: 'EXPENSE' | 'RECEIPT';
  notes: string;
  file_path: string;
  created_at: string;
}

interface Summary {
  totalExpenses: number;
  totalReceipts: number;
  balance: number;
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const { canView, canAdd, canEdit, canDelete } = usePermissions();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<Summary>({
    totalExpenses: 0,
    totalReceipts: 0,
    balance: 0,
  });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);

  const hasExpensePermission = canView('expenses');
  const companyId = user?.company?.id;

  useEffect(() => {
    if (hasExpensePermission && companyId) {
      fetchExpenses();
    }
  }, [companyId, startDate, endDate]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString().split('T')[0]);
      }

      const response = await api.get(`/expenses?${params.toString()}`);
      setExpenses(response.data.expenses);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
    } else {
      setEditingExpense(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingExpense(null);
    setError(null);
  };

  const handleDeleteClick = (expense: Expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!expenseToDelete) return;

    try {
      setLoading(true);
      await api.delete(`/expenses/${expenseToDelete.id}`);
      setSuccess('Expense deleted successfully');
      fetchExpenses();
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
    } catch (error) {
      console.error('Error deleting expense:', error);
      setError('Failed to delete expense');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id: number, filePath: string) => {
    try {
      setLoading(true);
      
      // If the file is already stored in Google Cloud Storage, we can just open it in a new tab
      if (filePath.startsWith('http')) {
        window.open(filePath, '_blank');
        setSuccess('Opening file in a new tab');
        setLoading(false);
        return;
      }
      
      // Otherwise, use the API to download the file from the server
      const response = await api.get(`/expenses/${id}/file`, {
        responseType: 'blob',
      });
      
      // Get the file extension from the response headers or default to .pdf
      const contentType = response.headers['content-type'];
      let fileExtension = '.pdf';
      if (contentType) {
        if (contentType.startsWith('image/')) {
          fileExtension = contentType.includes('jpeg') ? '.jpg' : '.png';
        } else if (contentType === 'application/pdf') {
          fileExtension = '.pdf';
        }
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_${id}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Expenses Management</Typography>
        {canAdd('expenses') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add New Entry
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'white' }}>
            <Typography variant="h6">Total Expenses</Typography>
            <Typography variant="h4">₹{summary.totalExpenses.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
            <Typography variant="h6">Total Receipts</Typography>
            <Typography variant="h4">₹{summary.totalReceipts.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: summary.balance >= 0 ? 'info.light' : 'warning.light', color: 'white' }}>
            <Typography variant="h6">Balance</Typography>
            <Typography variant="h4">₹{summary.balance.toFixed(2)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Date Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filter by Date</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </Paper>

      {/* Expenses Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{new Date(expense.date).toLocaleDateString()}</TableCell>
                <TableCell>{expense.type}</TableCell>
                <TableCell>{expense.notes}</TableCell>
                <TableCell align="right" sx={{ color: expense.type === 'EXPENSE' ? 'error.main' : 'success.main' }}>
                  ₹{expense.amount.toFixed(2)}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDownload(expense.id, expense.file_path)} color="primary">
                    <DownloadIcon />
                  </IconButton>
                  {canEdit('expenses') && (
                    <IconButton onClick={() => handleOpen(expense)} color="primary">
                      <EditIcon />
                    </IconButton>
                  )}
                  {canDelete('expenses') && (
                    <IconButton onClick={() => handleDeleteClick(expense)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ExpenseForm
        open={open}
        onClose={handleClose}
        onSubmit={fetchExpenses}
        expense={editingExpense}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this expense? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
} 